/**
 * Push opt-in gating (Pacote Funil, item 5) — the tiny PURE decision behind the one-time notification ask, split
 * out of main.ts so it is unit-testable without the DOM. The ask is offered at the FIRST MOMENT OF VICTORY (a
 * kill, a hoisted chest, or an outpost sale) or, failing those, after a stretch of active sea time. Every one of
 * those triggers routes through `shouldSchedulePushAsk`, which enforces the invariants that keep it non-intrusive:
 *   · at most ONE ask per session (once scheduled or already asked, never again),
 *   · never while a teaching layer owns the screen — a beat landing mid-Helm-Coach or with the Primer manual
 *     open is NOT scheduled; the next trigger (at latest the 4-min sea fallback) picks it up. (The old gate was
 *     "FTUE finished"; the quest-FTUE is gone, so the ask is decoupled from onboarding entirely.)
 *   · "Later"/refusal is permanent (the caller carries `asked=true` from the `sor:pushAsked` device flag).
 */
export interface PushAskState {
  /** The ask has already fired this session (or the device previously answered — the `sor:pushAsked` flag). */
  asked: boolean;
  /** An ask is already queued (a timer is pending) — a second victory beat must not double-schedule. */
  scheduled: boolean;
  /** A teaching layer owns the screen right now (Helm Coach guiding / Primer manual open) — never interrupt it. */
  teaching: boolean;
}

/** PURE: may we schedule the one-time push ask right now? True only when the captain has neither been asked nor
 *  already queued AND no teaching layer is up. Every victory-beat trigger AND the time fallback funnel through
 *  this. */
export function shouldSchedulePushAsk(s: PushAskState): boolean {
  return !s.asked && !s.scheduled && !s.teaching;
}
